import React from 'react';
import { ResultOnly } from '@containers/DigitalAnalyst/components/AnswerLine';
import styled from 'styled-components';
import { PlanSteps } from '@containers/DigitalAnalyst/AgentScriptEditor/Output';
import ErrorText from '@ui/ErrorText';
import { Container } from '@ui';
import { Working } from '@ui/Working';
import { isAgentOutputValid } from '@utils/agents';
import { AgentStepOutput } from '@containers/DigitalAnalyst/AgentScriptEditor/AgentStepOutput';
import { BASEURL } from '@services/network';
import { logError, logger } from '@utils/usefulFuncs';
import { createAuthedEventSource } from '@utils/streamAuth';

const AgentScriptResultWrapper = styled.div`
  padding: 24px 0 0 0;
  overflow: auto;
`;

const streamResult = async (URL, handlers = {}, options) => {
  const { onOpen, onMessage, onError, onComplete } = handlers;

  const source = createAuthedEventSource(URL, { headers: options?.headers });
  source.addEventListener('open', function (event) {
    try {
      onOpen && onOpen();
    } catch (e) {
      logError('error occurred in stream', e);
    }
  });
  source.addEventListener('error', function (error) {
    try {
      console.error('error message received in stream', error);
      onError && onError(error);
      source.close();
    } catch (e) {
      source.close();
      logError('error occurred in stream', e);
    }
  });
  source.addEventListener('message', function (event) {
    try {
      const { data } = event;
      onMessage && onMessage(data);
    } catch (e) {
      logError('error occurred in stream', e);
    }
  });
  source.addEventListener('complete', function (event) {
    try {
      onComplete && onComplete(event);
      source.close();
    } catch (e) {
      logError('error occurred in stream', e);
    }
  });
  return source;
};

export const AgentScriptResult = ({ resultId, onSetStreamStatus, onResultComplete }) => {
  const stream = React.useRef(null);
  const retryTimeoutRef = React.useRef(null);

  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [executedPlan, setExecutedPlan] = React.useState(null);
  const [isCompleted, setIsCompleted] = React.useState(false);

  // Safe JSON parse that logs errors and shows user-friendly message
  const safeJSONParse = (data, context = 'data') => {
    try {
      return { success: true, data: JSON.parse(data) };
    } catch (e) {
      console.error(`Failed to parse JSON in ${context}:`, e);
      console.error('Raw data:', data);
      setError('An unexpected error occurred, please try again later.');
      onSetStreamStatus && onSetStreamStatus('error');
      return { success: false, error: e };
    }
  };

  const getResult = async () => {
    if (stream?.current?.close) {
      console.log('closing stream');
      stream.current.close();
    }

    onSetStreamStatus && onSetStreamStatus('pending');

    setIsCompleted(false);
    setResult(null);
    setError(null);
    setExecutedPlan(null);

    const URL = `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(resultId)}&include_context=true&include_agent_plan=true`;
    const handlers = {
      onMessage: (data) => {
        const parseResult = safeJSONParse(data, 'onMessage');
        if (!parseResult.success) return;

        const streamData = parseResult.data;
        logger('AgentScriptResult onMessage', 'received', {
          runId: resultId,
          questionIndex: streamData?.question_index,
          finalAnswer: streamData?.final_answer === true,
          answerLength: streamData?.answer?.length || 0,
        });
        if (streamData?.answer) setResult(streamData.answer);
      },
      onError: (error) => {
        setError('Something went wrong, attempting to reconnect...');
        onSetStreamStatus && onSetStreamStatus('error');

        // Clear any existing retry timeout
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        // Set new retry timeout
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          getResult(resultId);
        }, 3000);
      },
      onComplete: (data) => {
        const parseResult = safeJSONParse(data.data, 'onComplete event.data');
        if (!parseResult.success) return;

        const response = parseResult.data.response;
        const answers = response?.answers || [];
        const row = answers.find((r) => r?.final_answer) || answers[0];
        if (!row) return;
        setResult(row.answer);
        onSetStreamStatus && onSetStreamStatus('complete');
        onResultComplete && onResultComplete(row.answer);
        if (row.agent_executed_plan) {
          setExecutedPlan(row.agent_executed_plan);
        }

        setIsCompleted(true);
      },
    };

    const source = streamResult(URL, handlers);
    if (stream.current && stream.current.close) {
      try {
        stream.current.close();
      } catch (e) {
        logError('error occurred while closing previous stream', e);
      }
    }
    stream.current = source;
  };

  React.useEffect(() => {
    if (!resultId) {
      return;
    }

    getResult(resultId);

    // Cleanup function to close stream and cancel retry timeouts on unmount
    return () => {
      // Close the stream if it exists
      if (stream.current && stream.current.close) {
        try {
          console.log('Cleaning up stream on unmount');
          stream.current.close();
          stream.current = null;
        } catch (e) {
          logError('error occurred while closing stream on unmount', e);
        }
      }

      // Clear any pending retry timeouts
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, [resultId]);

  if (!resultId) {
    return null;
  }

  const isResultAnAgentOutput = isAgentOutputValid(result);
  return (
    <AgentScriptResultWrapper>
      {error && (
        <Container bottom={16}>
          <ErrorText>{error}</ErrorText>
        </Container>
      )}
      {!!resultId && (!result || result === '') ? (
        <Container bottom={16}>
          <Working noVerticalPadding />
        </Container>
      ) : (
        <>
          {!isCompleted && (
            <Container bottom={16}>
              <Working noVerticalPadding />
            </Container>
          )}
          {isResultAnAgentOutput ? <AgentStepOutput output={result} /> : <ResultOnly result={result} />}
          {executedPlan && (
            <Container top={16}>
              <PlanSteps data={executedPlan} />
            </Container>
          )}
        </>
      )}
    </AgentScriptResultWrapper>
  );
};
